/**
 * Audit Logs API
 *
 * Endpoints for retrieving and exporting audit logs (Business tier feature).
 */

import { apiClient } from './client';

export interface AuditLog {
  id: string;
  actorType: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  details: Record<string, unknown> | null;
  deviceId: string | null;
  locationId: string | null;
  ipAddress: string | null;
  createdAt: string;
}

export interface AuditLogFilters {
  startDate: string;
  endDate: string;
  actorType?: string;
  action?: string;
  locationId?: string;
  limit?: number;
  offset?: number;
}

export interface AuditLogsResponse {
  logs: AuditLog[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface AuditLogStats {
  total: number;
  byCategory: { category: string; count: number }[];
  byActorType: { actorType: string; count: number }[];
}

function buildQueryParams(params: AuditLogFilters): string {
  const queryParams = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
  });

  if (params.actorType) {
    queryParams.set('actorType', params.actorType);
  }
  if (params.action) {
    queryParams.set('action', params.action);
  }
  if (params.locationId && params.locationId !== 'all') {
    queryParams.set('locationId', params.locationId);
  }
  if (params.limit) {
    queryParams.set('limit', params.limit.toString());
  }
  if (params.offset) {
    queryParams.set('offset', params.offset.toString());
  }

  return queryParams.toString();
}

export const auditLogsApi = {
  /**
   * List audit logs with filtering and pagination
   */
  async list(params: AuditLogFilters): Promise<AuditLogsResponse> {
    return apiClient.request(`/audit-logs?${buildQueryParams(params)}`);
  },

  /**
   * Get distinct action types for filtering
   */
  async getActions(): Promise<{ actions: string[] }> {
    return apiClient.request('/audit-logs/actions');
  },

  /**
   * Get audit log statistics
   */
  async getStats(params: { startDate: string; endDate: string }): Promise<AuditLogStats> {
    const queryParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
    });
    return apiClient.request(`/audit-logs/stats?${queryParams.toString()}`);
  },

  /**
   * Export audit logs as CSV
   */
  async export(params: AuditLogFilters): Promise<Blob> {
    const queryParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
    });

    if (params.actorType) {
      queryParams.set('actorType', params.actorType);
    }
    if (params.action) {
      queryParams.set('action', params.action);
    }
    if (params.locationId && params.locationId !== 'all') {
      queryParams.set('locationId', params.locationId);
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/audit-logs/export?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${(apiClient as unknown as { sessionToken: string }).sessionToken}`,
          'X-Store-Hash': (apiClient as unknown as { storeHash: string }).storeHash,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to export audit logs');
    }

    return response.blob();
  },
};
